import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ItemDetailDialog from "@/components/ItemDetailDialog";
import { api } from "@/lib/api";

/**
 * Public Inventory page.
 * - Lists every rentable item in the fleet.
 * - Each item shows which pods carry it (uses `stock_by_pod`).
 * - Filter by pod to see just what's available there.
 */
export default function Inventory() {
  const [items, setItems] = useState([]);
  const [pods, setPods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [podFilter, setPodFilter] = useState(""); // "" = all pods
  const [active, setActive] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/inventory"), api.get("/pods")])
      .then(([inv, p]) => { setItems(inv.data || []); setPods(p.data.items || []); })
      .finally(() => setLoading(false));
  }, []);

  const podBySlug = useMemo(() => Object.fromEntries(pods.map((p) => [p.slug, p])), [pods]);
  const livePods = pods.filter((p) => p.status === "active");

  const filtered = useMemo(() => {
    if (!podFilter) return items;
    return items.filter((it) => Number((it.stock_by_pod || {})[podFilter] || 0) > 0);
  }, [items, podFilter]);

  return (
    <div className="min-h-screen bg-white" data-testid="inventory-page">
      <Navbar />

      {/* HERO */}
      <section className="bg-brand-blue text-white py-16 md:py-20 border-b-8 border-brand-red">
        <div className="max-w-6xl mx-auto px-4">
          <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-gold mb-3">The Fleet</p>
          <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tightest leading-none">
            Every piece of gear <span className="text-brand-gold italic">we carry.</span>
          </h1>
          <p className="text-slate-200 text-sm md:text-base font-bold mt-5 max-w-2xl leading-relaxed">
            Commercial-grade tents, chairs, tables, linens, and chafing sets. Each item lives at one or more of our pods — filter below to see what&apos;s at yours.
          </p>
        </div>
      </section>

      {/* FILTER */}
      <section className="max-w-6xl mx-auto px-4 pt-10 pb-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 mr-2">Show items at</span>
          <button
            onClick={() => setPodFilter("")}
            className={`text-[10px] font-black uppercase tracking-widest px-4 py-2 rounded-full transition-colors ${podFilter === "" ? "bg-brand-red text-white" : "bg-slate-100 text-brand-blue hover:bg-slate-200"}`}
            data-testid="inv-filter-all"
          >
            All Pods
          </button>
          {livePods.map((p) => (
            <button
              key={p.slug}
              onClick={() => setPodFilter(p.slug)}
              className={`text-[10px] font-black uppercase tracking-widest px-4 py-2 rounded-full transition-colors ${podFilter === p.slug ? "bg-brand-red text-white" : "bg-slate-100 text-brand-blue hover:bg-slate-200"}`}
              data-testid={`inv-filter-${p.slug}`}
            >
              {p.name}
            </button>
          ))}
        </div>
        <span className="text-[10px] font-black uppercase tracking-widest text-slate-400" data-testid="inv-count">
          {filtered.length} item{filtered.length === 1 ? "" : "s"}
        </span>
      </section>

      {/* GRID */}
      <section className="max-w-6xl mx-auto px-4 pb-20">
        {loading ? (
          <div className="text-center py-16 text-slate-400 font-black uppercase text-xs tracking-widest" data-testid="inv-loading">
            <i className="fas fa-spinner fa-spin mr-2" /> Loading inventory…
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-slate-400 font-bold" data-testid="inv-empty">
            No items in stock at that pod yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="inv-grid">
            {filtered.map((item) => (
              <InventoryItem key={item.id} item={item} podBySlug={podBySlug} onOpen={() => setActive(item)} />
            ))}
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="bg-brand-slate py-16 border-t border-slate-100">
        <div className="max-w-3xl mx-auto text-center px-4">
          <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-red mb-3">Ready?</p>
          <h3 className="text-2xl md:text-3xl font-black uppercase tracking-tightest text-brand-blue mb-4">Book gear at your nearest pod.</h3>
          <Link
            to="/locations"
            className="inline-flex items-center gap-2 bg-brand-red text-white px-8 py-4 rounded-full text-sm font-black uppercase tracking-widest hover:bg-red-700 transition-colors"
            data-testid="inv-cta-locations"
          >
            <i className="fas fa-map-pin" /> View Locations
            <i className="fas fa-arrow-right" />
          </Link>
        </div>
      </section>

      <ItemDetailDialog item={active} open={!!active} onOpenChange={(o) => !o && setActive(null)} />
      <Footer />
    </div>
  );
}

function InventoryItem({ item, podBySlug, onOpen }) {
  const byPod = item.stock_by_pod || {};
  const availableAt = Object.entries(byPod)
    .filter(([, n]) => Number(n) > 0)
    .map(([slug]) => podBySlug[slug])
    .filter(Boolean);
  const soldOut = item.sold_out;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="group text-left bg-white rounded-[28px] overflow-hidden shadow-sm border border-slate-100 hover:shadow-2xl hover:-translate-y-1 transition-all focus:outline-none focus:ring-4 focus:ring-brand-blue/20"
      data-testid={`inv-item-${item.id}`}
    >
      <div className="h-52 bg-slate-100 relative overflow-hidden">
        {item.image_url ? (
          <img src={item.image_url} alt={item.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-brand-blue">
            <i className={`fas ${item.icon || "fa-box"} text-5xl`} />
          </div>
        )}
        {soldOut && (
          <span className="absolute top-3 left-3 bg-slate-900 text-white text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full">Fully Booked</span>
        )}
        {item.instant_booking && !soldOut && (
          <span className="absolute top-3 left-3 bg-brand-gold text-brand-blue text-[9px] font-black uppercase tracking-widest px-2.5 py-1 rounded-full">
            <i className="fas fa-bolt text-[8px] mr-1" /> Instant
          </span>
        )}
      </div>

      <div className="p-5 space-y-3">
        <div>
          <h4 className="font-black text-base text-brand-blue uppercase tracking-tightest leading-tight">{item.name}</h4>
          <p className="text-[11px] text-slate-500 font-medium mt-1.5 line-clamp-2 min-h-[32px]">{item.description}</p>
        </div>

        <div className="flex items-baseline justify-between">
          <p className="text-2xl font-black text-brand-red">
            ${item.price_per_day.toFixed(2)}
            <span className="text-[10px] text-slate-400 font-bold ml-1">/day</span>
          </p>
          {item.extras && item.extras.length > 0 && (
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">+ add-ons</span>
          )}
        </div>

        <div className="pt-3 border-t border-slate-100">
          <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1.5">Available At</p>
          {availableAt.length === 0 ? (
            <span className="text-[10px] font-bold text-slate-400">Not currently stocked</span>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {availableAt.map((p) => (
                <span key={p.slug} className="text-[9px] font-black uppercase tracking-widest bg-brand-blue/10 text-brand-blue px-2 py-1 rounded-full" data-testid={`inv-item-${item.id}-pod-${p.slug}`}>
                  <i className="fas fa-map-pin mr-1" /> {p.name}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </button>
  );
}
